import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UserPlus, Loader2 } from "lucide-react";
import { PasswordField } from "@/components/auth/PasswordField";
import { useRegisterForm } from "@/hooks/auth/useRegisterForm";
import { FullScreenLoader } from "@/components/ui/full-screen-loader";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import HubfloBanner from "@/components/ui/HubfloBanner";
import { useEffect } from "react";

// Registration component
const Register = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    form,
    isLoading,
    showLoadingScreen,
    loadingMessages,

    onSubmit,
  } = useRegisterForm();

  // If user is already logged in but we're not in a loading state, redirect to home
  // This prevents interrupting the registration loading process
  useEffect(() => {
    if (user && !showLoadingScreen) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, showLoadingScreen]);

  // Don't render the form if user is already logged in and we're not loading
  if (user && !showLoadingScreen) {
    return null;
  }

  const handleEmailChange = (e) => {
    const value = e.target.value;
    form.setValue("email", value);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Show loading overlay when processing registration */}
      {showLoadingScreen && (
        <FullScreenLoader
          isLoading={true}
          messages={loadingMessages}
          minDisplayTime={500}
        />
      )}

      {/* Reusable banner component */}
      <HubfloBanner showAnimation={true} />

      {/* Content section with proper padding */}
      <div className="flex-1 flex flex-col items-center p-4 pt-8">
        <div className="max-w-md w-full mx-auto pb-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold">Let's get started</h1>
            <p className="text-gray-600 mt-2 text-sm">
              You're just a few steps away from automating your work and growing
              your business with AI Agents.
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <GoogleSignInButton buttonText="Sign up with Google" />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">OR</span>
              </div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="you@example.com"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          handleEmailChange(e);
                        }}
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <PasswordField
                form={form}
                name="password"
                label="Password"
                disabled={isLoading}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    Get started
                  </span>
                )}
              </Button>

              <div className="text-xs text-gray-500 text-center mt-4">
                By signing up, you agree to our{" "}
                <a
                  href="https://www.hubflo.com/utility-pages/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700 hover:underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://www.hubflo.com/utility-pages/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700 hover:underline"
                >
                  Privacy Policy
                </a>
              </div>
            </form>
          </Form>

          <div className="mt-8 text-center">
            <div className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-[#ecb22d] hover:text-[#d9a429] hover:underline font-medium"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
